// src/pages/Chat.jsx
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1.
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import socket from '../services/socket';
import { listMyChats, getChat } from '../services/chatApi';
import ChatWindow from '../components/chat/ChatWindow';
import NewChatModal from '../components/chat/NewChatModal';


const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    plus:   <Icon size={14}><path d="M12 5v14" /><path d="M5 12h14" /></Icon>,
    search: <Icon size={14}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>,
    x:      <Icon size={14}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>,
    chat:   <Icon size={20}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Icon>,
};


function chatLabel(chat, meId) {
    if (chat.type === 'group') return chat.name || 'Grupo';
    const other = (chat.participants || []).find(p => String(p._id) !== String(meId));
    return other?.name || 'Chat directo';
}

function lastLine(chat) {
    const lm = chat.lastMessage;
    if (!lm || !lm.at) return 'Sin mensajes aún';
    const text = lm.content || (lm.hasAttachments ? '📎 Adjunto' : '');
    return text.length > 60 ? text.slice(0, 60) + '…' : text;
}

function relTime(iso) {
    if (!iso) return '';
    const d  = new Date(iso);
    const s  = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60)      return 'ahora';
    if (s < 3600)    return `${Math.floor(s / 60)}m`;
    if (s < 86400)   return `${Math.floor(s / 3600)}h`;
    if (s < 604800)  return `${Math.floor(s / 86400)}d`;
    return d.toLocaleDateString();
}


const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.ch-root {
    --ch-accent: var(--brand-700, #0a4aa8);
    --ch-accent-hover: var(--brand-800, #07357f);
    --ch-accent-light: #dbeafe;
    --ch-accent-ring: rgba(10, 74, 168, .12);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 1300px; margin: 0 auto; padding: 20px 20px 24px;
    display: flex; flex-direction: column;
    height: calc(100vh - 64px); min-height: 400px;
    color: var(--c-text-1);
}
.ch-root * { box-sizing: border-box; }
.ch-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 14px; flex: 0 0 auto;
}
.ch-title { font-size: 22px; font-weight: 700; margin: 0; letter-spacing: -0.01em; }

.ch-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid transparent; border-radius: 8px;
    padding: 8px 14px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s;
}
.ch-btn-primary { background: var(--ch-accent); color: #fff; border-color: var(--ch-accent); box-shadow: 0 1px 3px rgba(10,74,168,.25); }
.ch-btn-primary:hover { background: var(--ch-accent-hover); border-color: var(--ch-accent-hover); transform: translateY(-1px); }

.ch-shell {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04);
    display: grid; grid-template-columns: 340px 1fr;
    flex: 1; min-height: 0; overflow: hidden;
}

.ch-list-pane {
    border-right: 1px solid var(--c-border);
    display: flex; flex-direction: column; min-height: 0;
}
.ch-search-row {
    padding: 10px;
    border-bottom: 1px solid var(--c-card-header-border);
    flex: 0 0 auto;
}
.ch-search {
    display: flex; align-items: center; gap: 8px;
    padding: 7px 12px;
    background: var(--c-input-bg);
    border: 1px solid var(--c-border);
    border-radius: 999px;
    color: var(--c-text-3);
    transition: all .15s;
}
.ch-search:focus-within { border-color: var(--ch-accent); box-shadow: 0 0 0 3px var(--ch-accent-ring); background: var(--c-card); }
.ch-search input { flex: 1; border: none; outline: none; background: transparent; color: var(--c-text-1); font-size: 13px; font-family: inherit; min-width: 0; }
.ch-search-clear { background: none; border: none; cursor: pointer; color: var(--c-text-4); padding: 2px; display: inline-flex; border-radius: 4px; }
.ch-search-clear:hover { color: var(--c-text-1); }

.ch-list {
    overflow-y: auto; flex: 1; min-height: 0;
}
.ch-item {
    padding: 12px 14px;
    border-bottom: 1px solid var(--c-card-header-border);
    cursor: pointer; position: relative;
    display: flex; justify-content: space-between; gap: 10px;
    transition: background .15s;
}
.ch-item:hover { background: var(--c-msg-bg); }
.ch-item.active { background: var(--ch-accent-light); }
.ch-item.active::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0;
    width: 3px; background: var(--ch-accent);
}
.ch-item-body { flex: 1; min-width: 0; }
.ch-item-top {
    display: flex; align-items: center; gap: 6px; margin-bottom: 3px;
}
.ch-item-name {
    font-weight: 600; font-size: 13.5px; color: var(--c-text-1);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ch-item-group-tag {
    background: var(--c-msg-bg); color: var(--c-text-3);
    border: 1px solid var(--c-border);
    border-radius: 4px; padding: 1px 6px;
    font-size: 9.5px; font-weight: 600;
    letter-spacing: 0.3px; text-transform: uppercase;
    flex-shrink: 0;
}
.ch-item-preview {
    font-size: 12px; color: var(--c-text-3);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ch-item-right {
    text-align: right; display: flex; flex-direction: column;
    align-items: flex-end; gap: 4px; flex-shrink: 0;
}
.ch-item-time { font-size: 11px; color: var(--c-text-4); font-variant-numeric: tabular-nums; }
.ch-item-unread {
    background: #dc2626; color: #fff;
    min-width: 18px; height: 18px; padding: 0 6px;
    border-radius: 999px;
    font-size: 10.5px; font-weight: 700;
    display: inline-flex; align-items: center; justify-content: center;
}

.ch-empty {
    color: var(--c-text-4); padding: 28px 16px;
    font-size: 13px; text-align: center;
}

.ch-window-pane { overflow: hidden; min-height: 0; }
.ch-window-empty {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    height: 100%; color: var(--c-text-4);
    gap: 12px; padding: 24px;
}
.ch-window-empty-icon { opacity: 0.4; }
.ch-window-empty-text { font-size: 14px; }

@media (max-width: 900px) {
    .ch-shell { grid-template-columns: 280px 1fr; }
}
@media (max-width: 700px) {
    .ch-shell { grid-template-columns: 1fr; }
    .ch-window-pane:not(:empty) { position: absolute; inset: 0; }
}
`;


export default function Chat() {
    const { user } = useContext(AuthContext);
    const meId     = user?.id;
    const [chats, setChats]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [activeId, setActiveId] = useState(null);
    const [showNew, setShowNew]   = useState(false);
    const [search, setSearch]     = useState('');

    const activeIdRef = useRef(null);
    useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

    const reload = () => listMyChats().then(setChats).catch(() => setChats([]));

    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const onCreated = (chat) => {
            setChats(prev => {
                if (prev.some(c => String(c._id) === String(chat._id))) return prev;
                return [chat, ...prev];
            });
        };
        const onUpdated = (chat) => {
            setChats(prev => prev.map(c => String(c._id) === String(chat._id) ? { ...c, ...chat } : c));
        };
        const onRemoved = ({ chatId }) => {
            setChats(prev => prev.filter(c => String(c._id) !== String(chatId)));
            if (String(activeIdRef.current) === String(chatId)) setActiveId(null);
        };
        const onMessage = ({ chatId, message }) => {
            setChats(prev => {
                const idx = prev.findIndex(c => String(c._id) === String(chatId));
                if (idx < 0) {
                    getChat(chatId).then(fresh => {
                        setChats(p => p.some(c => String(c._id) === String(fresh._id)) ? p : [fresh, ...p]);
                    }).catch(() => {});
                    return prev;
                }
                const base = prev[idx];
                const isMine = String(message.sender?._id || message.sender) === String(meId);
                const isActive = String(activeIdRef.current) === String(chatId);
                const updated = {
                    ...base,
                    lastMessage: {
                        sender: message.sender?._id || message.sender,
                        content: message.content || (message.attachments?.[0]?.name ? `📎 ${message.attachments[0].name}` : '📎 Adjunto'),
                        at: message.createdAt,
                        hasAttachments: (message.attachments || []).length > 0,
                    },
                    lastMessageAt: message.createdAt,
                    unreadCount: (!isMine && !isActive) ? (base.unreadCount || 0) + 1 : (base.unreadCount || 0),
                };
                const next = [...prev];
                next.splice(idx, 1);
                return [updated, ...next];
            });
        };
        const onMessageDeleted = ({ chatId }) => {
            getChat(chatId).then(fresh => {
                setChats(prev => prev.map(c => String(c._id) === String(chatId) ? { ...c, ...fresh } : c));
            }).catch(() => {});
        };

        socket.on('chatCreated', onCreated);
        socket.on('chatUpdated', onUpdated);
        socket.on('chatRemoved', onRemoved);
        socket.on('messageCreated', onMessage);
        socket.on('messageDeleted', onMessageDeleted);
        return () => {
            socket.off('chatCreated', onCreated);
            socket.off('chatUpdated', onUpdated);
            socket.off('chatRemoved', onRemoved);
            socket.off('messageCreated', onMessage);
            socket.off('messageDeleted', onMessageDeleted);
        };
    }, [meId]);

    const openChat = (id) => {
        setActiveId(id);
        setChats(prev => prev.map(c => String(c._id) === String(id) ? { ...c, unreadCount: 0 } : c));
    };

    const active = useMemo(() => chats.find(c => String(c._id) === String(activeId)) || null, [chats, activeId]);

    const visibleChats = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return chats;
        return chats.filter(c => {
            const label = chatLabel(c, meId).toLowerCase();
            const lastContent = (c.lastMessage?.content || '').toLowerCase();
            return label.includes(q) || lastContent.includes(q);
        });
    }, [chats, search, meId]);

    return (
        <>
            <style>{CSS}</style>
            <div className="ch-root">
                <div className="ch-header">
                    <h1 className="ch-title">Chat interno</h1>
                    <button type="button" className="ch-btn ch-btn-primary" onClick={() => setShowNew(true)}>
                        {I.plus} Nuevo chat
                    </button>
                </div>

                <div className="ch-shell">
                    <div className="ch-list-pane">
                        <div className="ch-search-row">
                            <div className="ch-search">
                                {I.search}
                                <input
                                    type="text"
                                    placeholder="Buscar chat…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                                {search && (
                                    <button type="button" className="ch-search-clear" onClick={() => setSearch('')} aria-label="Limpiar">
                                        {I.x}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="ch-list">
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: 32 }}><Spinner animation="border" size="sm" /></div>
                            ) : visibleChats.length === 0 ? (
                                <p className="ch-empty">
                                    {chats.length === 0
                                        ? 'No tienes chats aún. Crea uno con "+ Nuevo chat".'
                                        : 'Sin coincidencias para tu búsqueda.'}
                                </p>
                            ) : (
                                visibleChats.map(c => (
                                    <div key={c._id}
                                        onClick={() => openChat(c._id)}
                                        className={`ch-item${String(c._id) === String(activeId) ? ' active' : ''}`}
                                    >
                                        <div className="ch-item-body">
                                            <div className="ch-item-top">
                                                <span className="ch-item-name">{chatLabel(c, meId)}</span>
                                                {c.type === 'group' && <span className="ch-item-group-tag">Grupo</span>}
                                            </div>
                                            <div className="ch-item-preview">{lastLine(c)}</div>
                                        </div>
                                        <div className="ch-item-right">
                                            <span className="ch-item-time">{relTime(c.lastMessageAt)}</span>
                                            {c.unreadCount > 0 && (
                                                <span className="ch-item-unread">{c.unreadCount}</span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="ch-window-pane">
                        {active ? (
                            <ChatWindow
                                chat={active}
                                onChatChanged={(updated) => {
                                    setChats(prev => prev.map(c => String(c._id) === String(updated._id) ? { ...c, ...updated } : c));
                                }}
                                onChatRemoved={(id) => {
                                    setChats(prev => prev.filter(c => String(c._id) !== String(id)));
                                    if (String(activeId) === String(id)) setActiveId(null);
                                }}
                            />
                        ) : (
                            <div className="ch-window-empty">
                                <span className="ch-window-empty-icon">{I.chat}</span>
                                <span className="ch-window-empty-text">Selecciona un chat para empezar</span>
                            </div>
                        )}
                    </div>
                </div>

                <NewChatModal
                    show={showNew}
                    onClose={() => setShowNew(false)}
                    onCreated={(chat) => {
                        setChats(prev => {
                            if (prev.some(c => String(c._id) === String(chat._id))) return prev;
                            return [chat, ...prev];
                        });
                        setActiveId(chat._id);
                    }}
                />
            </div>
        </>
    );
}
